import React, { useState, useEffect } from 'react';
import { Card } from '../../components/Card';
import { Loading } from '../../components/Loading';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Users, Clock, Shield, Heart, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { exportService } from '../../services/exportService';
import { apiClient } from '../../services/api';
import { getRoleDisplayName } from '@/utils/roleUtils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface KpiMetric {
  name: string;
  displayName: string;
  value: number;
  previousValue?: number;
  changePercentage: number | null;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  category: string;
  icon: string;
  color: string;
  isPositiveTrend: boolean;
}

interface ExecutiveDashboard {
  summary: {
    totalTicketsToday: number;
    totalTicketsThisMonth: number;
    ticketGrowthPercentage: number | null;
    averageResponseTimeHours: number | null;
    responseTimeChangePercentage: number | null;
    slaCompliancePercentage: number | null;
    slaComplianceChangePercentage: number | null;
    customerSatisfactionScore: number | null;
    satisfactionChangePercentage: number | null;
    activeAgentsCount: number;
    teamUtilizationPercentage: number | null;
  };
  keyMetrics: KpiMetric[];
  trends: {
    ticketVolumeTrend: Array<{ date: string; value: number; label: string }>;
    responseTimeTrend: Array<{ date: string; value: number; label: string }>;
    slaComplianceTrend: Array<{ date: string; value: number; label: string }>;
    satisfactionTrend: Array<{ date: string; value: number; label: string }>;
  };
  topPerformers: Array<{
    userId: string;
    name: string;
    role: string;
    avatarUrl: string;
    ticketsResolved: number;
    averageResponseTime: number;
    satisfactionScore: number;
    performanceScore: number;
  }>;
  alerts: Array<{
    type: string;
    severity: string;
    title: string;
    message: string;
    createdAt: string;
    actionUrl: string;
    isRead: boolean;
  }>;
  customerSatisfaction: {
    averageScore: number;
    totalSurveys: number;
    responseRate: number;
    netPromoterScore: number;
    ratingBreakdown: Array<{ rating: number; count: number; percentage: number }>;
  };
}

const ExecutiveDashboard: React.FC = () => {
  useDocumentTitle('Executive Dashboard | SupportDesk');
  const [dashboard, setDashboard] = useState<ExecutiveDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: '30d', to: 'now' });
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        fromDate: getDateFromRange(dateRange.from),
        toDate: getDateFromRange(dateRange.to)
      });

      const response = await apiClient.get(`/analytics/executive/dashboard?${params.toString()}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const fromDate = new Date(getDateFromRange(dateRange.from));
      const toDate = new Date(getDateFromRange(dateRange.to));

      await exportService.exportExecutiveDashboard({
        fromDate,
        toDate,
      });
    } catch (error) {
      console.error('Export failed:', error);
      // Could add toast notification here
    } finally {
      setIsExporting(false);
    }
  };

  const getDateFromRange = (range: string): string => {
    const now = new Date();
    if (range === 'now') return now.toISOString();

    const days = parseInt(range.replace('d', ''));
    const date = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return date.toISOString();
  };

  const getIcon = (iconName: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'users': <Users className="w-6 h-6" />,
      'clock': <Clock className="w-6 h-6" />,
      'shield-check': <Shield className="w-6 h-6" />,
      'heart': <Heart className="w-6 h-6" />,
      'check-circle': <CheckCircle className="w-6 h-6" />,
      'ticket': <CheckCircle className="w-6 h-6" />
    };
    return iconMap[iconName] || <CheckCircle className="w-6 h-6" />;
  };

  const getTrendIcon = (trend: string, isPositive: boolean) => {
    if (trend === 'stable') return null;
    return trend === 'up' ? (
      <TrendingUp className={`w-4 h-4 ${isPositive ? 'text-green-500' : 'text-red-500'}`} />
    ) : (
      <TrendingDown className={`w-4 h-4 ${isPositive ? 'text-green-500' : 'text-red-500'}`} />
    );
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" text="Loading executive dashboard..." />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Dashboard Unavailable</h2>
          <p className="text-gray-600">Unable to load dashboard data. Please try again later.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      {/* Title and actions stack below sm, and the actions themselves wrap, so the header
          stays inside a 320px viewport rather than forcing a page-level horizontal
          scrollbar (BugHerd #147). gap replaces space-x so wrapped rows keep a gutter. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Executive Dashboard</h1>
          <p className="text-gray-600">High-level overview of support performance</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <select
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="365d">Last year</option>
          </select>

          <Button
            onClick={fetchDashboardData}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>

          <Button
            onClick={handleExport}
            variant="primary"
            size="sm"
            disabled={isExporting || loading}
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {dashboard.alerts.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <h3 className="font-medium text-yellow-800">Active Alerts</h3>
          </div>
          <div className="mt-2 space-y-1">
            {dashboard.alerts.slice(0, 3).map((alert, index) => (
              <div key={index} className="text-sm text-yellow-700">
                {alert.title}: {alert.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboard.keyMetrics.filter(metric => !/satisfaction|utilization|productivity/i.test(metric.name + metric.displayName)).map((metric, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div style={{ color: metric.color }}>
                {getIcon(metric.icon)}
              </div>
              <div className="flex items-center space-x-1">
                {getTrendIcon(metric.trend, metric.isPositiveTrend)}
                <span className={`text-sm font-medium ${
                  metric.isPositiveTrend ? 'text-green-600' : 'text-red-600'
                }`}>
                  {metric.changePercentage != null ? `${Math.abs(metric.changePercentage)}%` : '-'}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-2xl font-bold text-gray-900">
                {metric.value.toLocaleString()}{metric.unit}
              </div>
              <div className="text-sm text-gray-600">{metric.displayName}</div>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Volume Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Ticket Volume Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dashboard.trends.ticketVolumeTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Response Time Trend */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Response Time Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dashboard.trends.responseTimeTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#F59E0B" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SLA Compliance */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">SLA Compliance Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dashboard.trends.slaComplianceTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Customer Satisfaction */}

      </div>

      {/* Top Performers */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Top Performers</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tickets Resolved
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Response Time
                </th>


              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboard.topPerformers.map((performer, index) => (
                <tr key={performer.userId}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                          {performer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {performer.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getRoleDisplayName(performer.role)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {performer.ticketsResolved}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {performer.averageResponseTime.toFixed(1)}h
                  </td>


                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default ExecutiveDashboard;
